// src/context/NotificationContext.js
// Context pour gérer les notifications - NE PAS MODIFIER

import React, { createContext, useState, useCallback } from 'react';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Ajouter une notification
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      type: notification.type || 'info', // success, error, warning, info
      title: notification.title,
      message: notification.message,
      duration: notification.duration || 5000, // ms
      action: notification.action, // { label, callback }
      createdAt: new Date(),
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-dismiss après la durée spécifiée
    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  // Retirer une notification
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Notification de succès
  const showSuccess = useCallback((title, message, duration = 5000) => {
    return addNotification({ type: 'success', title, message, duration });
  }, [addNotification]);

  // Notification d'erreur
  const showError = useCallback((title, message, duration = 5000) => {
    return addNotification({ type: 'error', title, message, duration });
  }, [addNotification]);

  // Notification d'avertissement
  const showWarning = useCallback((title, message, duration = 5000) => {
    return addNotification({ type: 'warning', title, message, duration });
  }, [addNotification]);

  // Notification d'info
  const showInfo = useCallback((title, message, duration = 5000) => {
    return addNotification({ type: 'info', title, message, duration });
  }, [addNotification]);

  // Notification persistante (ne disparaît pas automatiquement)
  const showPersistent = useCallback((type, title, message) => {
    return addNotification({ type, title, message, duration: 0 });
  }, [addNotification]);

  // Effacer toutes les notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPersistent,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};